import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { CommonModule } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { DialogHeaderExportComponent } from "../../../../shared/modules/dialog/dialog-header-export/dialog-header-export.component";
import { SharedModule } from "../../../../shared/shared.module";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { RadioCardComponent } from "../../../../shared/components/radio-card/radio-card.component";
import { WidgetHelperService } from "../../services/widget-helper.service";
import { CA_DATA } from "../../models/data.model";
import { exportCaNMoinsExcel } from "./ca-nmoins-excel.utils";

const ROW_PALETTE = [
  "#4e79a7", "#f28e2b", "#e15759", "#76b7b2", "#59a14f",
  "#edc948", "#b07aa1", "#ff9da7", "#9c755f", "#bab0ac",
  "#8cd17d", "#a0cbe8",
];

export type CaNMoinsPeriodScale = "j" | "s" | "m" | "a";

export interface CaNMoinsDialogData {
  rawData: CA_DATA["data"];
  initialPeriod: CaNMoinsPeriodScale;
  timeScale: string;
  timeScaleLabel: string;
  exerciceStart?: string;
}

export interface ClientRow {
  denomination: string;
  caN: number;
  caN1: number;
  deltaEur: number;
  deltaPct: number | null;
}

export interface NMoinsPeriodRow {
  relKey: string;
  label: string;
  labelN1: string;
  anchor: Date;
  caN: number;
  caN1: number;
  deltaEur: number;
  deltaPct: number | null;
  clients: ClientRow[];
  nbClients: number;
  color: string;
}

@Component({
  selector: "vl-ca-nmoins-dialog",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    DialogHeaderExportComponent,
    ChipsToggleComponent,
    RadioCardComponent,
    MatIcon,
    MatTooltip,
  ],
  templateUrl: "./ca-nmoins-dialog.component.html",
  styleUrl: "./ca-nmoins-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CaNMoinsDialogComponent {
  data = inject<CaNMoinsDialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<CaNMoinsDialogComponent>);

  exporting = signal(false);

  scale = signal<CaNMoinsPeriodScale>(this.data.initialPeriod);
  scaleElements = [
    { name: "Jour", value: "j" as CaNMoinsPeriodScale },
    { name: "Semaine", value: "s" as CaNMoinsPeriodScale },
    { name: "Mois", value: "m" as CaNMoinsPeriodScale },
    { name: "Année", value: "a" as CaNMoinsPeriodScale },
  ];
  scaleLabel: Record<CaNMoinsPeriodScale, string> = { j: "Jour", s: "Semaine", m: "Mois", a: "Année" };
  scaleLabelPlural: Record<CaNMoinsPeriodScale, string> = { j: "jours", s: "semaines", m: "mois", a: "années" };
  scaleBestLabel: Record<CaNMoinsPeriodScale, string> = { j: "Meilleure progression (jour)", s: "Meilleure progression (sem.)", m: "Meilleure progression (mois)", a: "Meilleure progression (an)" };
  scaleLabelSingularLower: Record<CaNMoinsPeriodScale, string> = { j: "jour", s: "semaine", m: "mois", a: "année" };

  expandedPeriods = signal<Set<string>>(new Set());
  togglePeriod(key: string): void {
    const cur = new Set(this.expandedPeriods());
    if (cur.has(key)) cur.delete(key); else cur.add(key);
    this.expandedPeriods.set(cur);
  }

  isExpanded(key: string): boolean {
    return this.expandedPeriods().has(key);
  }

  private getRelativeKey(periodLabel: string, period: string): string {
    switch (period) {
      case "m": return periodLabel.split("-")[0];
      case "j": return periodLabel.split("-").slice(0, 2).join("-");
      case "s": return periodLabel.replace(/ - \d{4}$/, "");
      case "a": return periodLabel;
      default: return periodLabel;
    }
  }

  rows = computed<NMoinsPeriodRow[]>(() => {
    const period = this.scale();
    const rawAll = this.data.rawData ?? [];

    const { data: currentFiltered, refDate } = WidgetHelperService.filterByTimeScale(
      rawAll as any,
      this.data.timeScale,
      this.data.exerciceStart,
    ) as { data: CA_DATA["data"]; refDate: Date };
    const now = new Date();
    const compStart = new Date(refDate);
    compStart.setFullYear(compStart.getFullYear() - 1);
    const compEnd = new Date(now);
    compEnd.setFullYear(compEnd.getFullYear() - 1);

    const n1Filtered = rawAll.filter((d) => {
      const dt = new Date(d.date);
      return dt >= compStart && dt < compEnd;
    });

    // Group current data by period + client
    const currentByKey = new Map<string, {
      anchor: Date;
      relKey: string;
      ca: number;
      clientCa: Map<string, number>;
    }>();

    for (const d of currentFiltered) {
      const dt = new Date(d.date);
      if (isNaN(dt.getTime())) continue;
      const key = WidgetHelperService.getDateForPeriod(d.date, period);
      if (!key) continue;
      const relKey = this.getRelativeKey(key, period);
      let bucket = currentByKey.get(relKey);
      if (!bucket) {
        bucket = { anchor: dt, relKey, ca: 0, clientCa: new Map() };
        currentByKey.set(relKey, bucket);
      }
      if (dt < bucket.anchor) bucket.anchor = dt;
      const m = d.montant ?? 0;
      bucket.ca += m;
      const client = d.tiersDenomination || "Inconnu";
      bucket.clientCa.set(client, (bucket.clientCa.get(client) ?? 0) + m);
    }

    // Group N-1 data by period + client
    const n1ByRelKey = new Map<string, { ca: number; label: string; clientCa: Map<string, number> }>();
    for (const d of n1Filtered) {
      const dt = new Date(d.date);
      if (isNaN(dt.getTime())) continue;
      const key = WidgetHelperService.getDateForPeriod(d.date, period);
      if (!key) continue;
      const relKey = this.getRelativeKey(key, period);
      let bucket = n1ByRelKey.get(relKey);
      if (!bucket) {
        bucket = { ca: 0, label: key, clientCa: new Map() };
        n1ByRelKey.set(relKey, bucket);
      }
      const m = d.montant ?? 0;
      bucket.ca += m;
      const client = d.tiersDenomination || "Inconnu";
      bucket.clientCa.set(client, (bucket.clientCa.get(client) ?? 0) + m);
    }

    const buildClients = (
      nMap: Map<string, number> | undefined,
      n1Map: Map<string, number> | undefined,
    ): ClientRow[] => {
      const allDenoms = new Set([...(nMap?.keys() ?? []), ...(n1Map?.keys() ?? [])]);
      const rows: ClientRow[] = [];
      for (const denom of allDenoms) {
        const caN = nMap?.get(denom) ?? 0;
        const caN1 = n1Map?.get(denom) ?? 0;
        const dEur = caN - caN1;
        const dPct = caN1 > 0 ? (dEur / caN1) * 100 : (caN > 0 ? null : 0);
        rows.push({ denomination: denom, caN, caN1, deltaEur: dEur, deltaPct: dPct });
      }
      return rows.sort((a, b) => b.caN - a.caN);
    };

    const out: NMoinsPeriodRow[] = [];

    for (const [relKey, b] of currentByKey) {
      const n1 = n1ByRelKey.get(relKey);
      const caN1 = n1?.ca ?? 0;
      const deltaEur = b.ca - caN1;
      const deltaPct = caN1 > 0 ? ((deltaEur / caN1) * 100) : (b.ca > 0 ? null : 0);
      const clients = buildClients(b.clientCa, n1?.clientCa);

      const fullKey = WidgetHelperService.getDateForPeriod(b.anchor.toISOString(), period);
      out.push({
        relKey,
        label: this.formatPeriodLabel(fullKey ?? relKey, b.anchor, period),
        labelN1: n1?.label ?? "—",
        anchor: b.anchor,
        caN: b.ca,
        caN1,
        deltaEur,
        deltaPct,
        clients,
        nbClients: clients.length,
        color: "",
      });
    }

    for (const [relKey, n1] of n1ByRelKey) {
      if (!currentByKey.has(relKey)) {
        const anchor = new Date(n1.label);
        const clients = buildClients(undefined, n1.clientCa);
        out.push({
          relKey,
          label: "—",
          labelN1: n1.label,
          anchor: isNaN(anchor.getTime()) ? new Date() : anchor,
          caN: 0,
          caN1: n1.ca,
          deltaEur: -n1.ca,
          deltaPct: -100,
          clients,
          nbClients: clients.length,
          color: "",
        });
      }
    }

    const rankedByCA = [...out].sort((a, b) => b.caN - a.caN);
    rankedByCA.forEach((r, i) => { r.color = ROW_PALETTE[i % ROW_PALETTE.length]; });

    return out.sort((a, b) => b.anchor.getTime() - a.anchor.getTime());
  });

  stats = computed(() => {
    const rs = this.rows();
    const totalN = rs.reduce((s, r) => s + r.caN, 0);
    const totalN1 = rs.reduce((s, r) => s + r.caN1, 0);
    const globalDeltaEur = totalN - totalN1;
    const globalDeltaPct = totalN1 > 0 ? ((globalDeltaEur / totalN1) * 100) : null;

    const withPositiveDelta = rs.filter((r) => r.deltaEur > 0 && r.caN1 > 0);
    const bestGrowth = withPositiveDelta.length > 0
      ? withPositiveDelta.sort((a, b) => b.deltaEur - a.deltaEur)[0]
      : null;

    return {
      totalN,
      totalNFormatted: this.formatEuro(totalN),
      totalN1,
      totalN1Formatted: this.formatEuro(totalN1),
      globalDeltaEur,
      globalDeltaPct,
      globalDeltaLabel: globalDeltaPct !== null
        ? `${globalDeltaPct >= 0 ? "+" : ""}${globalDeltaPct.toFixed(1)}%`
        : (totalN > 0 ? "nouveau" : "—"),
      bestGrowthLabel: bestGrowth?.label ?? "—",
      bestGrowthDelta: bestGrowth ? `+${this.formatEuro(bestGrowth.deltaEur)}` : "—",
      count: rs.length,
    };
  });

  colorForDelta(deltaPct: number | null): string {
    if (deltaPct === null) return "#9db8c3";
    if (deltaPct >= 10) return "#10b981";
    if (deltaPct > 0) return "#4ade80";
    if (deltaPct === 0) return "#9db8c3";
    if (deltaPct > -10) return "#f97316";
    return "#dc2626";
  }

  private formatPeriodLabel(key: string, anchor: Date, period: CaNMoinsPeriodScale): string {
    switch (period) {
      case "j": return anchor.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
      case "s": return key;
      case "m": return anchor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
      case "a": return anchor.toLocaleDateString("fr-FR", { year: "numeric" });
    }
  }

  formatEuro(v: number): string {
    const n = Number(v ?? 0);
    if (!isFinite(n)) return "—";
    return WidgetHelperService.formatNumeric(n) + " €";
  }

  formatEuroExact(v: number): string {
    const n = Number(v ?? 0);
    if (!isFinite(n)) return "—";
    return `${n.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
  }

  formatDeltaPct(v: number | null): string {
    if (v === null) return "—";
    return `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;
  }

  async exportExcel(): Promise<void> {
    this.exporting.set(true);
    try {
      await exportCaNMoinsExcel({
        rows: this.rows(),
        stats: this.stats(),
        scaleLabel: this.scaleLabel[this.scale()],
        timeScaleLabel: this.data.timeScaleLabel,
      });
    } finally {
      this.exporting.set(false);
    }
  }
}
